// Pure match normalization. No Vue, no network, no metadata of its own: the
// hero/item name lookups are passed in so this module can run anywhere
// (main thread, tests, and later a worker).
//
// Normalization mutates the freshly decoded records in place and returns the
// same objects. The previous implementation deep-cloned every match, which
// doubled the object graph for 50,000 matches. Nothing retains the raw decoded
// objects, so the clone was pure cost. Cached records are owned by the cache
// and must be treated as read-only by consumers.

export const TEAM = {
  red: 1,
  blue: 2,
}

// Non-enumerable so it never shows up in spreads, JSON or v-for.
const NORMALIZED_FLAG = '__kopNormalized'

export const isNormalized = (match) => Boolean(match && match[NORMALIZED_FLAG])

const markNormalized = (match) => {
  Object.defineProperty(match, NORMALIZED_FLAG, { value: true, enumerable: false, configurable: true })
}

/**
 * Parses the API's string-encoded id lists ("[0,1,2]") into numbers.
 *
 * - Numeric id zero is preserved (it is a valid hero/item id).
 * - "" / "[]" / null yield an empty array instead of `[0]`, which is what the
 *   old split-based parser produced for "[]" because "" coerces to 0.
 * - An array is passed through (already parsed) with its entries coerced to
 *   numbers, so normalizing twice is safe.
 */
export const parseIdList = (value) => {
  if (value === null || value === undefined) return []

  if (Array.isArray(value)) {
    const out = []
    for (const entry of value) {
      if (typeof entry === 'number') {
        out.push(entry)
      } else if (typeof entry === 'string' && entry.trim() !== '') {
        out.push(Number(entry))
      }
    }
    return out
  }

  if (typeof value === 'number') return [value]

  if (typeof value !== 'string') return []

  const ids = []
  let numberStart = -1
  for (let i = 0; i <= value.length; i++) {
    const code = i < value.length ? value.charCodeAt(i) : -1
    // digits, '-', '.', 'e', 'E', '+' are part of a number token
    const isNumberChar =
      (code >= 48 && code <= 57) || code === 45 || code === 46 || code === 43 || code === 101 || code === 69
    if (isNumberChar) {
      if (numberStart === -1) numberStart = i
    } else if (numberStart !== -1) {
      ids.push(Number(value.slice(numberStart, i)))
      numberStart = -1
    }
  }
  return ids
}

export const teamName = (team) => (team === TEAM.blue ? 'blue' : 'red')

/**
 * Normalizes one decoded match in place and returns it.
 *
 * Adds: winnerName, bans (hero names, replacing the encoded string),
 * averageMmr, playedAtMs; per player: itemName (item names), teamName,
 * heroName, won. The `items` string is kept so existing `player.items`
 * truthiness checks still work.
 *
 * Idempotent: a record that was already normalized is returned unchanged,
 * which matters because `bans` changes type from string to array.
 *
 * @param {object} match decoded match record
 * @param {{ getHeroName: (id: number) => string, getItemName: (id: number) => string | undefined, getAverageMmr?: (match: object) => number | null }} helpers
 */
export const normalizeMatch = (match, helpers) => {
  if (!match || typeof match !== 'object') return match
  if (isNormalized(match)) return match

  const { getHeroName, getItemName } = helpers

  if (match.winner) {
    match.winnerName = match.winner === TEAM.blue ? 'blue' : 'red'
  }
  const winnerName = match.winnerName

  // Kept string bans stay in place when falsy (null/undefined/""), exactly as
  // before; "[]" and real lists become arrays of hero names.
  if (match.bans) {
    const banIds = parseIdList(match.bans)
    const bans = new Array(banIds.length)
    for (let i = 0; i < banIds.length; i++) bans[i] = getHeroName(banIds[i])
    match.bans = bans
  }

  const players = Array.isArray(match.MatchData) ? match.MatchData : null
  if (players) {
    for (let i = 0; i < players.length; i++) {
      const player = players[i]
      if (!player || typeof player !== 'object') continue

      if (player.items) {
        const itemIds = parseIdList(player.items)
        const itemName = new Array(itemIds.length)
        for (let j = 0; j < itemIds.length; j++) itemName[j] = getItemName(itemIds[j])
        player.itemName = itemName
      } else if (!Array.isArray(player.itemName)) {
        // Consumers iterate itemName unconditionally; an empty list is safer
        // than `undefined` and carries the same meaning.
        player.itemName = []
      }

      if (player.team) {
        player.teamName = player.team === TEAM.blue ? 'blue' : 'red'
      }

      player.heroName = getHeroName(player.heroId)

      if (player.teamName && winnerName) {
        player.won = player.teamName === winnerName
      }
    }
  }

  match.averageMmr = (helpers.getAverageMmr || getMatchAverageMmr)(match)

  // Parsed once here so hot paths (weekly boundary, daily counts, recency
  // sort, freshness) never re-parse the ISO string. NaN when missing/invalid.
  const playedAtMs = match.playedAtUtc ? Date.parse(match.playedAtUtc) : NaN
  match.playedAtMs = Number.isNaN(playedAtMs) ? NaN : playedAtMs

  markNormalized(match)
  return match
}

/**
 * Normalizes every record in place and returns the same array (no second
 * array is allocated). Synchronous: use for small pages. Bulk datasets should
 * go through `normalizeMatchesChunked` so the main thread can breathe.
 */
export const normalizeMatches = (matches, helpers) => {
  if (!Array.isArray(matches)) return []
  for (let i = 0; i < matches.length; i++) normalizeMatch(matches[i], helpers)
  return matches
}

/**
 * Chunked, yielding variant for bulk datasets. `processInChunks` is injected
 * so this module stays free of scheduling concerns.
 *
 * @returns {Promise<{ matches: object[], processed: number, chunks: number, duration: number, aborted: boolean }>}
 */
export const normalizeMatchesChunked = async (matches, helpers, processInChunks, options) => {
  const list = Array.isArray(matches) ? matches : []
  const stats = await processInChunks(list, (match) => normalizeMatch(match, helpers), options)
  return { matches: list, ...stats }
}

/**
 * Average MMR of the players in a match. Prefers a precomputed value from
 * the API when present (several historical spellings), otherwise averages the
 * finite player MMR values. `null` when nothing is available.
 */
export const getMatchAverageMmr = (match) => {
  const precomputedAverage =
    match?.averageMmr ?? match?.avgMmr ?? match?.averageMMR ?? match?.avgMMR ?? match?.matchAverageMmr
  if (precomputedAverage !== undefined && precomputedAverage !== null && !Number.isNaN(Number(precomputedAverage))) {
    return Number(precomputedAverage)
  }

  const players = match?.MatchData
  if (!Array.isArray(players) || players.length === 0) return null

  let sum = 0
  let count = 0
  for (let i = 0; i < players.length; i++) {
    const mmr = Number(players[i]?.mmr)
    if (Number.isFinite(mmr)) {
      sum += mmr
      count++
    }
  }

  if (count === 0) return null
  return sum / count
}

export default {
  TEAM,
  isNormalized,
  parseIdList,
  teamName,
  normalizeMatch,
  normalizeMatches,
  normalizeMatchesChunked,
  getMatchAverageMmr,
}
